'use client';

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Autocomplete, Chip, CircularProgress, TextField } from '@/primitives';
import {
  alpha,
  useTheme,
  type SxProps,
  type Theme,
} from '@mui/material/styles';
import { setDefaultHeader } from '@/publicSearch/graphql/headers';
import { useQuickSearchMembersLazyQuery } from '@/publicSearch/graphql/queries.generated';
import usePublicSearchContext from '@/publicSearch/store/usePublicSearchContext';
import { debounce } from 'lodash';
import { quickSearchInputSx } from '../search/constants';
import {
  QUICK_SEARCH_MIN_LENGTH,
  QUICK_SEARCH_TOP_RESULTS,
  QUICK_SEARCH_TYPEAHEAD_DEBOUNCE_MS,
} from '../search/quickSearchConfig';
import { IDENT_CHIP_PLACEHOLDER } from '../search/quickSearchCopy';
import {
  getWildcardLabel,
  isMessageOption,
  isWildcardOption,
  resolveExplicitWildcards,
  resolveWildcard,
  type MessageOption,
  type WildcardOption,
} from '../search/identChipWildcardUi';
import { normalizeWildcardInput } from '../search/animalIdentChipFilter';
import { autocompleteSizeSx } from './autocompleteSizeSx';
import { toSxArray } from '@/lib/sx';

type MemberChipOption = {
  memberId: number;
  memberIdent: string;
  memberName?: string | null;
};

type MemberChipValue = MemberChipOption | string;

function getChipLabel(chip: MemberChipValue): string {
  if (typeof chip === 'string') return chip;
  const ident = chip.memberIdent ?? '';
  const name = chip.memberName ?? '';
  if (ident && name) return `${ident} - ${name}`;
  return ident || name || '';
}

function serializeChips(chips: MemberChipValue[]): string {
  return chips
    .map((chip) =>
      typeof chip === 'string'
        ? chip
        : (chip.memberIdent ?? chip.memberName ?? ''),
    )
    .filter(Boolean)
    .join(',');
}

function parseValueToChips(value: string): MemberChipValue[] {
  return value
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
}

interface MemberIdentMultiSelectProps {
  label?: string;
  placeholder?: string;
  value: string;
  onChange: (value: string) => void;
  inputRef?: React.Ref<HTMLInputElement>;
  disabled?: boolean;
  sx?: SxProps<Theme>;
  size?: 'small' | 'medium';
  ariaRequired?: boolean;
  showExplicitWildcardOptions?: boolean;
}

export default function MemberIdentMultiSelect({
  label,
  placeholder = IDENT_CHIP_PLACEHOLDER,
  value,
  onChange,
  inputRef,
  disabled,
  sx,
  size = 'medium',
  ariaRequired = false,
  showExplicitWildcardOptions = false,
}: MemberIdentMultiSelectProps) {
  const theme = useTheme();
  const orgId = usePublicSearchContext((context) => context.society.orgId);
  const society = usePublicSearchContext((context) => context.society);
  const societyId =
    society.societyId ?? (society.orgId ? Number(society.orgId) : undefined);
  const [quickSearch, { data, loading }] = useQuickSearchMembersLazyQuery();

  const [chipValues, setChipValues] = useState<MemberChipValue[]>(() =>
    parseValueToChips(value),
  );
  const [inputValue, setInputValue] = useState<string>('');
  const lastEmittedRef = useRef<string>(value);

  useEffect(() => {
    if (value !== lastEmittedRef.current) {
      // Controlled prop sync when parent clears or replaces the value externally.
      // eslint-disable-next-line react-hooks/set-state-in-effect -- ref-guarded controlled-prop sync: re-parses chips when the parent clears/replaces value externally; chipValues is also set by the Autocomplete onChange, so it cannot be a pure derived value
      setChipValues(parseValueToChips(value));
      lastEmittedRef.current = value;
    }
  }, [value]);

  const options = useMemo(
    () => (data?.quickSearchMembers ?? []) as MemberChipOption[],
    [data?.quickSearchMembers],
  );

  const runSearch = useMemo(
    () =>
      debounce((term: string) => {
        const trimmed = term.trim();
        if (trimmed.length < QUICK_SEARCH_MIN_LENGTH || !orgId || !societyId) {
          return;
        }
        quickSearch({
          variables: {
            searchTerm: trimmed,
            societyId,
            top: QUICK_SEARCH_TOP_RESULTS,
          },
          context: { headers: setDefaultHeader(orgId) },
        });
      }, QUICK_SEARCH_TYPEAHEAD_DEBOUNCE_MS),
    [orgId, societyId, quickSearch],
  );

  useEffect(() => () => runSearch.cancel(), [runSearch]);

  const emitChange = useCallback(
    (chips: MemberChipValue[]) => {
      const serialized = serializeChips(chips);
      lastEmittedRef.current = serialized;
      onChange(serialized);
    },
    [onChange],
  );

  const handleInputChange = useCallback(
    (_: React.SyntheticEvent, newInputValue: string) => {
      if (newInputValue.includes(',')) {
        const parts = newInputValue
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean);
        if (parts.length > 0) {
          const seen = new Set(
            chipValues.map((c) =>
              typeof c === 'string' ? c : (c.memberIdent ?? ''),
            ),
          );
          const toAdd = parts.filter((p) => !seen.has(p));
          if (toAdd.length > 0) {
            const next = [...chipValues, ...toAdd];
            setChipValues(next);
            emitChange(next);
          }
          setInputValue('');
          runSearch.cancel();
          return;
        }
      }

      setInputValue(newInputValue);
      const normalized = normalizeWildcardInput(newInputValue);
      if (normalized.includes('%')) {
        runSearch.cancel();
      } else {
        runSearch(newInputValue);
      }
    },
    [chipValues, emitChange, runSearch],
  );

  const handleChange = useCallback(
    (
      _: React.SyntheticEvent,
      newValues: (MemberChipValue | WildcardOption | MessageOption)[],
    ) => {
      const seen = new Set<string>();
      const deduped: MemberChipValue[] = [];
      for (const item of newValues) {
        if (isMessageOption(item)) continue;
        const normalized: MemberChipValue = isWildcardOption(item)
          ? item.pattern
          : item;
        const key =
          typeof normalized === 'string'
            ? normalized.trim()
            : (normalized.memberIdent ?? '');
        if (!key || seen.has(key)) continue;
        seen.add(key);
        deduped.push(
          typeof normalized === 'string' ? normalized.trim() : normalized,
        );
      }
      setChipValues(deduped);
      emitChange(deduped);
    },
    [emitChange],
  );

  const commitPendingInputOnBlur = useCallback(() => {
    const term = inputValue.trim();
    if (!term) return;
    const wildcard = resolveWildcard(term);
    if (!wildcard) return;
    const pattern = wildcard.pattern.trim();
    const seen = new Set(
      chipValues
        .map((c) => (typeof c === 'string' ? c.trim() : (c.memberIdent ?? '')))
        .filter(Boolean),
    );
    if (seen.has(pattern)) {
      setInputValue('');
      return;
    }
    const next = [...chipValues, pattern];
    setChipValues(next);
    emitChange(next);
    setInputValue('');
  }, [chipValues, emitChange, inputValue]);

  return (
    <Autocomplete<MemberChipValue, true, false, true>
      multiple
      freeSolo
      openOnFocus={false}
      noOptionsText=""
      value={chipValues}
      inputValue={inputValue}
      onInputChange={handleInputChange}
      onChange={handleChange as any}
      options={options as MemberChipValue[]}
      filterOptions={(baseOptions) => {
        const term = normalizeWildcardInput(inputValue).trim();
        const hasPercent = term.includes('%');
        const result: (MemberChipValue | WildcardOption | MessageOption)[] =
          hasPercent ? [] : [...baseOptions];

        if (showExplicitWildcardOptions && !hasPercent) {
          for (const w of resolveExplicitWildcards(term)) result.push(w);
        } else {
          const wildcard = resolveWildcard(term);
          if (wildcard && (hasPercent || baseOptions.length > 0)) {
            result.push(wildcard);
          }
        }

        if (result.length === 0 && !loading) {
          const base = term.replace(/^%+|%+$/g, '').trim();
          const tooShortForWildcard =
            hasPercent && base.length < QUICK_SEARCH_MIN_LENGTH;
          const tooShortForSearch =
            !hasPercent && term.length < QUICK_SEARCH_MIN_LENGTH;
          if (
            !tooShortForWildcard &&
            !tooShortForSearch &&
            term.length >= QUICK_SEARCH_MIN_LENGTH
          ) {
            result.push({ _isMessage: true, text: 'No members found' });
          }
        }
        return result as MemberChipValue[];
      }}
      getOptionLabel={(option) => {
        if (isWildcardOption(option)) return option.pattern;
        if (isMessageOption(option)) return option.text;
        if (typeof option === 'string') return option;
        return getChipLabel(option);
      }}
      getOptionDisabled={(option) => isMessageOption(option as unknown)}
      isOptionEqualToValue={(option, val) => {
        if (typeof option === 'string' && typeof val === 'string') {
          return option === val;
        }
        if (typeof option !== 'string' && typeof val !== 'string') {
          return option.memberId === val.memberId;
        }
        return false;
      }}
      disabled={disabled}
      loading={loading}
      sx={[
        quickSearchInputSx,
        { flex: 1, minWidth: 160, ...autocompleteSizeSx(size) },
        ...toSxArray(sx),
      ]}
      renderValue={(tagValue, getItemProps) =>
        tagValue.map((option, index) => {
          const { key, ...tagProps } = getItemProps({ index });
          return (
            <Chip
              key={key}
              label={
                typeof option === 'string'
                  ? option
                  : (option.memberIdent ?? option.memberName ?? '')
              }
              size="small"
              sx={(t) => ({
                backgroundColor: (t.vars ?? t).palette.background.default,
                ...(size === 'small'
                  ? {
                      height: 22,
                      '& .MuiChip-label': { px: 0.75, fontSize: '12px' },
                    }
                  : {}),
              })}
              {...tagProps}
            />
          );
        })
      }
      renderInput={(params) => (
        <TextField
          {...params}
          size={size}
          label={label}
          placeholder={chipValues.length === 0 ? placeholder : undefined}
          inputRef={inputRef}
          slotProps={{
            ...params.slotProps,
            htmlInput: {
              ...params.slotProps.htmlInput,
              ...(ariaRequired ? { 'aria-required': true as const } : {}),
              onBlur: (e: React.FocusEvent<HTMLInputElement>) => {
                params.slotProps.htmlInput?.onBlur?.(e);
                const rt = e.relatedTarget as HTMLElement | null;
                if (rt?.closest?.('.MuiAutocomplete-listbox')) return;
                commitPendingInputOnBlur();
              },
            },
            input: {
              ...params.slotProps.input,
              endAdornment: (
                <>
                  {loading ? (
                    <CircularProgress
                      color="inherit"
                      size={size === 'small' ? 16 : 20}
                    />
                  ) : null}
                  {params.slotProps.input.endAdornment}
                </>
              ),
            },
          }}
        />
      )}
      renderOption={(props, option) => {
        const { key, ...optionProps } = props;
        if (isMessageOption(option)) {
          return (
            <li
              key={key}
              {...optionProps}
              style={{
                pointerEvents: 'none',
                color: alpha(theme.palette.text.primary, 0.38),
                fontStyle: 'italic',
                fontSize: '13px',
              }}
            >
              {option.text}
            </li>
          );
        }
        if (isWildcardOption(option)) {
          return (
            <li key={key} {...optionProps} style={{ fontStyle: 'italic' }}>
              {getWildcardLabel(option)}
            </li>
          );
        }
        const optLabel =
          typeof option === 'string' ? option : getChipLabel(option);
        return (
          <li key={key} {...optionProps}>
            {optLabel}
          </li>
        );
      }}
    />
  );
}
